import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import clsx from "clsx";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { FixedSizeList, type ListChildComponentProps } from "react-window";
import { format, formatDistanceToNow, parseISO } from "date-fns";

import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Divider from "@material-ui/core/Divider";
import Drawer from "@material-ui/core/Drawer";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Paper from "@material-ui/core/Paper";
import Switch from "@material-ui/core/Switch";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import CloseIcon from "@material-ui/icons/Close";
import FilterListIcon from "@material-ui/icons/FilterList";
import ShareIcon from "@material-ui/icons/Share";

import Dialog from "src/components/Dialog";
import ConfirmDialog from "src/components/ConfirmDialog";
import LoadableButton from "src/components/LoadableButton";
import ErrorAlertsCustomDateRange from "src/pages/support/ErrorAlertsCustomDateRange";
import ErrorAlertsSearchFilters from "src/pages/support/ErrorAlertsSearchFilters";
import ErrorAlertsTimeline from "src/pages/support/ErrorAlertsTimeline";
import {
  buildErrorAlertShareUrl,
  buildErrorAlertsPath,
  getErrorAlertsPresetRange,
  mergeErrorAlertsUrlState,
  parseErrorAlertsUrlState,
  type ErrorAlertsDatePreset,
  type ErrorAlertsUrlState,
} from "src/pages/support/errorAlertsUrlState";
import {
  supportGetErrorAlerts,
  supportSendErrorAlertToSlack,
  supportUpdateErrorAlertTriage,
  supportUpdateErrorAlertVisibility,
} from "src/apiService/modules/support";
import {
  getErrorAlertFilterValueLabel,
  type ErrorAlertSearchFieldId,
  type SupportErrorAlertRecord,
  type SupportErrorAlertSearchFilter,
  type SupportErrorAlertsPage,
  type SupportErrorAlertTimeline,
  type SupportErrorAlertTriageStatus,
} from "src/interfaces/supportErrorAlerts.interface";
import { setSupportLoginToken } from "src/store/support/actions";
import { supportGetToken } from "src/store/support/selector";
import { formatErrorAlertSlackMessage } from "src/utils/errorAlertSlackMessage";

const COLUMN_ORDER: SupportErrorAlertTriageStatus[] = [
  "untriaged",
  "known",
  "escalated",
  "resolved",
];

const COLUMN_LABELS: Record<SupportErrorAlertTriageStatus, string> = {
  untriaged: "Untriaged",
  known: "Known issue",
  escalated: "Escalated to dev",
  resolved: "Resolved",
};

const ROW_HEIGHT = 188;
const COLUMN_HEIGHT = 620;

const useStyles = makeStyles((theme) => ({
  page: {
    padding: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(2),
  },
  filtersRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  searchField: {
    minWidth: 320,
    flex: "1 1 320px",
  },
  boardArea: {
    position: "relative",
  },
  boardToolbar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  cardLegend: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
  },
  legendSwatch: {
    width: 4,
    height: 16,
    borderRadius: 2,
  },
  legendSwatchNew: {
    backgroundColor: "#f57c00",
  },
  legendSwatchNotNew: {
    backgroundColor: theme.palette.grey[400],
  },
  boardSummary: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginLeft: "auto",
  },
  boardRefreshing: {
    opacity: 0.55,
    pointerEvents: "none",
  },
  boardOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
  },
  boardScroll: {
    display: "flex",
    gap: theme.spacing(2),
    overflowX: "auto",
    paddingBottom: theme.spacing(1),
  },
  column: {
    minWidth: 320,
    width: 320,
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
  },
  columnActive: {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
  },
  columnHeader: {
    padding: theme.spacing(1.5, 2),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  columnBody: {
    padding: theme.spacing(1),
    height: COLUMN_HEIGHT,
  },
  emptyState: {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.text.secondary,
    textAlign: "center",
    padding: theme.spacing(2),
  },
  cardOuter: {
    padding: theme.spacing(0.75),
  },
  cardOuterDragging: {
    visibility: "hidden",
  },
  card: {
    height: ROW_HEIGHT - theme.spacing(1.5),
    borderLeft: "4px solid transparent",
    cursor: "pointer",
    padding: theme.spacing(1.5),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    overflow: "hidden",
  },
  visibleStripe: {
    borderLeftColor: "#f57c00",
  },
  hiddenStripe: {
    borderLeftColor: theme.palette.grey[400],
  },
  cardDragging: {
    boxShadow: theme.shadows[8],
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  cardHeaderMeta: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  cardTimestamp: {
    whiteSpace: "nowrap",
  },
  errorPreview: {
    fontFamily: "monospace",
    fontSize: 12,
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    lineHeight: 1.4,
    minHeight: 50,
  },
  cardFooter: {
    marginTop: "auto",
  },
  cardFooterLabel: {
    color: theme.palette.text.secondary,
  },
  drawerPaper: {
    width: 460,
    maxWidth: "100vw",
  },
  detailHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
  },
  detailBody: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  detailSection: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  detailActionRow: {
    display: "flex",
    alignItems: "stretch",
    gap: theme.spacing(1),
  },
  detailActionButton: {
    height: 36,
    boxSizing: "border-box",
  },
  detailPrimaryAction: {
    flex: 1,
  },
  shareButton: {
    minWidth: 36,
    width: 36,
    padding: 0,
    flexShrink: 0,
  },
  shareButtonWrap: {
    display: "flex",
  },
  detailTopControls: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(2),
  },
  detailTopField: {
    flex: 1,
  },
  detailTopSwitch: {
    marginLeft: 0,
    marginRight: 0,
    minHeight: 40,
  },
  readonlyField: {
    "& textarea": {
      fontFamily: "monospace",
      fontSize: 12,
    },
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(6),
  },
  previewBlock: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    fontFamily: "monospace",
    fontSize: 12,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  extraFieldsBlock: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    fontFamily: "monospace",
    fontSize: 12,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
}));

function formatRelativeTime(value: string | null) {
  if (!value) return "Unknown time";
  return formatDistanceToNow(parseISO(value), { addSuffix: true });
}

function formatDateTime(value: string | null) {
  if (!value) return "Unknown";
  return format(parseISO(value), "MMM d, yyyy h:mm a");
}

function formatSlackPreview(alert: SupportErrorAlertRecord, note: string) {
  return formatErrorAlertSlackMessage({
    alert,
    userEmail: alert.userEmail || alert.user,
    note,
    supportPageUrl: buildErrorAlertShareUrl(alert.id, alert.createdAt),
  });
}

function isFailedPreconditionError(error: any) {
  return error?.code === "failed-precondition";
}

function canApplyDetailFilter(value: string) {
  const trimmed = value.trim();
  return (
    trimmed.length > 0 && trimmed !== "Unknown" && trimmed !== "Not provided"
  );
}

interface DetailFilterFieldProps {
  label: string;
  value: string;
  filterField: ErrorAlertSearchFieldId;
  filterValue: string;
  onApplyFilter: (field: ErrorAlertSearchFieldId, value: string) => void;
  disabled?: boolean;
  helperText?: string;
}

function DetailFilterField({
  label,
  value,
  filterField,
  filterValue,
  onApplyFilter,
  disabled = false,
  helperText,
}: DetailFilterFieldProps) {
  const canFilter = canApplyDetailFilter(filterValue);

  return (
    <TextField
      label={label}
      fullWidth
      variant="outlined"
      size="small"
      margin="dense"
      value={value}
      helperText={helperText}
      InputProps={{
        readOnly: true,
        endAdornment: canFilter ? (
          <InputAdornment position="end">
            <Tooltip title="Add filter">
              <span>
                <IconButton
                  size="small"
                  edge="end"
                  disabled={disabled}
                  onClick={() => onApplyFilter(filterField, filterValue)}
                >
                  <FilterListIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </InputAdornment>
        ) : undefined,
      }}
    />
  );
}

interface ErrorAlertCardContentProps {
  alert: SupportErrorAlertRecord;
  className?: string;
}

function ErrorAlertCardContent({
  alert,
  className,
}: ErrorAlertCardContentProps) {
  const classes = useStyles();

  return (
    <Paper
      className={clsx(
        classes.card,
        alert.isVisibleToUser ? classes.visibleStripe : classes.hiddenStripe,
        className
      )}
      elevation={1}
    >
      <Box className={classes.cardHeader}>
        <Box className={classes.cardHeaderMeta}>
          <Chip
            size="small"
            label={alert.platform || "Unknown"}
            color="primary"
            variant="outlined"
          />
          {alert.sentToSlack ? (
            <Tooltip
              title={`Sent to Slack ${formatRelativeTime(alert.sentToSlack)}`}
            >
              <ChatBubbleOutlineIcon fontSize="small" color="action" />
            </Tooltip>
          ) : null}
        </Box>
        <Typography
          variant="caption"
          color="textSecondary"
          className={classes.cardTimestamp}
        >
          {formatRelativeTime(alert.createdAt)}
        </Typography>
      </Box>

      <Typography variant="body2" className={classes.errorPreview}>
        {alert.errorMessage || "No technical error message captured."}
      </Typography>

      <Box className={classes.cardFooter}>
        <Typography variant="body2">{alert.userEmail || alert.user}</Typography>
        <Typography variant="caption" className={classes.cardFooterLabel}>
          {alert.isVisibleToUser ? "New" : "Not new"}
        </Typography>
      </Box>
    </Paper>
  );
}

interface ErrorAlertCardProps {
  alert: SupportErrorAlertRecord;
  onClick: () => void;
}

function ErrorAlertCard({ alert, onClick }: ErrorAlertCardProps) {
  const classes = useStyles();
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: alert.id,
    data: { triageStatus: alert.triageStatus, alertId: alert.id },
  });

  return (
    <div
      ref={setNodeRef}
      className={clsx(
        classes.cardOuter,
        isDragging && classes.cardOuterDragging
      )}
      {...listeners}
      {...attributes}
      onClick={(event) => {
        if (event.defaultPrevented || isDragging) return;
        onClick();
      }}
    >
      <ErrorAlertCardContent alert={alert} />
    </div>
  );
}

interface ColumnListData {
  alerts: SupportErrorAlertRecord[];
  onSelect: (alert: SupportErrorAlertRecord) => void;
}

function ColumnRow({
  index,
  style,
  data,
}: ListChildComponentProps<ColumnListData>) {
  const alert = data.alerts[index];

  return (
    <div style={style}>
      <ErrorAlertCard alert={alert} onClick={() => data.onSelect(alert)} />
    </div>
  );
}

interface ErrorAlertsColumnProps {
  status: SupportErrorAlertTriageStatus;
  alerts: SupportErrorAlertRecord[];
  onSelect: (alert: SupportErrorAlertRecord) => void;
}

function ErrorAlertsColumn({
  status,
  alerts,
  onSelect,
}: ErrorAlertsColumnProps) {
  const classes = useStyles();
  const { isOver, setNodeRef } = useDroppable({
    id: status,
  });

  return (
    <Paper
      ref={setNodeRef}
      className={clsx(classes.column, isOver && classes.columnActive)}
      elevation={0}
    >
      <Box className={classes.columnHeader}>
        <Typography variant="subtitle1">{COLUMN_LABELS[status]}</Typography>
        <Chip size="small" label={alerts.length} />
      </Box>
      <Divider />
      <Box className={classes.columnBody}>
        {alerts.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant="body2">No alerts in this column.</Typography>
          </Box>
        ) : (
          <FixedSizeList
            height={COLUMN_HEIGHT - 16}
            itemCount={alerts.length}
            itemData={{ alerts, onSelect }}
            itemSize={ROW_HEIGHT}
            width="100%"
          >
            {ColumnRow}
          </FixedSizeList>
        )}
      </Box>
    </Paper>
  );
}

interface SendToSlackDialogContentProps {
  alert: SupportErrorAlertRecord;
  onClose: () => void;
  onSent: (note: string) => Promise<void>;
}

function SendToSlackDialogContent({
  alert,
  onClose,
  onSent,
}: SendToSlackDialogContentProps) {
  const classes = useStyles();

  const formik = useFormik({
    initialValues: {
      note: "",
    },
    validateOnMount: true,
    onSubmit: async (values, helpers) => {
      await onSent(values.note);
      helpers.resetForm();
      onClose();
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <DialogTitle>Send to Slack</DialogTitle>
      <DialogContent>
        <Box mb={2}>
          <Typography variant="body2" color="textSecondary">
            Target: configured dev/support alerts channel via support backend.
          </Typography>
        </Box>
        {alert.sentToSlack ? (
          <Box mb={2}>
            <Typography color="secondary" variant="body2">
              This alert was already sent to Slack{" "}
              {formatRelativeTime(alert.sentToSlack)}.
            </Typography>
          </Box>
        ) : null}
        <TextField
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          margin="dense"
          label="Add context"
          name="note"
          value={formik.values.note}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        />
        <Box mt={2}>
          <Typography variant="subtitle2" gutterBottom>
            Preview
          </Typography>
          <Box className={classes.previewBlock}>
            {formatSlackPreview(alert, formik.values.note)}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadableButton
          type="submit"
          color="primary"
          variant="contained"
          loading={formik.isSubmitting}
          disabled={formik.isSubmitting || !formik.isValid}
        >
          Send to Slack
        </LoadableButton>
      </DialogActions>
    </form>
  );
}

interface SendToSlackDialogProps {
  alert: SupportErrorAlertRecord | null;
  open: boolean;
  onClose: () => void;
  onSent: (note: string) => Promise<void>;
}

function SendToSlackDialog({
  alert,
  open,
  onClose,
  onSent,
}: SendToSlackDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      {open && alert ? (
        <SendToSlackDialogContent
          alert={alert}
          onClose={onClose}
          onSent={onSent}
        />
      ) : null}
    </Dialog>
  );
}

function ErrorAlerts() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const token = useSelector(supportGetToken);
  const history = useHistory();
  const location = useLocation();

  const urlState = useMemo(
    () => parseErrorAlertsUrlState(location.pathname, location.search),
    [location.pathname, location.search]
  );
  const { preset, from, to, filters, alertId } = urlState;

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [alerts, setAlerts] = useState<SupportErrorAlertRecord[]>([]);
  const [timeline, setTimeline] = useState<
    SupportErrorAlertTimeline | undefined
  >();
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [selectedBucketStart, setSelectedBucketStart] = useState<string | null>(
    null
  );
  const [savingAlertId, setSavingAlertId] = useState<string | null>(null);
  const [pendingVisibilityChange, setPendingVisibilityChange] = useState<{
    alert: SupportErrorAlertRecord;
    isVisibleToUser: boolean;
  } | null>(null);
  const [slackDialogOpen, setSlackDialogOpen] = useState(false);
  const [activeDragAlertId, setActiveDragAlertId] = useState<string | null>(
    null
  );

  const queryKey = useMemo(
    () =>
      `${from.toISOString()}|${to.toISOString()}|${JSON.stringify(filters)}`,
    [filters, from, to]
  );

  useEffect(() => {
    setSelectedBucketStart(null);
  }, [queryKey]);

  const navigateUrl = useCallback(
    (patch: Partial<ErrorAlertsUrlState>) => {
      const nextState = mergeErrorAlertsUrlState(urlState, patch);
      const { pathname, search } = buildErrorAlertsPath(nextState);
      history.replace({ pathname, search });
    },
    [history, urlState]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  const handleError = useCallback(
    (error: any, fallback: string) => {
      if (isFailedPreconditionError(error)) {
        dispatch(setSupportLoginToken());
      }
      toast.error(error?.message || fallback);
    },
    [dispatch]
  );

  const fetchAlerts = useCallback(
    async ({
      fromDate,
      toDate,
      cursor,
      nextFilters,
    }: {
      fromDate: Date;
      toDate: Date;
      cursor?: string;
      nextFilters: SupportErrorAlertSearchFilter[];
    }) => {
      if (!token) return;

      if (cursor) setLoadingMore(true);
      else setLoading(true);

      try {
        const result = await supportGetErrorAlerts(
          token,
          fromDate.toISOString(),
          toDate.toISOString(),
          {
            limit: 100,
            ...(cursor ? { cursor } : {}),
            ...(nextFilters.length ? { filters: nextFilters } : {}),
          }
        );
        const page: SupportErrorAlertsPage = result.data;
        setAlerts((prev) => (cursor ? [...prev, ...page.alerts] : page.alerts));
        setNextCursor(page.nextCursor);
        if (!cursor && page.timeline) setTimeline(page.timeline);
      } catch (error) {
        if (!cursor) {
          setAlerts([]);
          setNextCursor(null);
          setTimeline(undefined);
        }
        handleError(error, "Failed to load error alerts");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [handleError, token]
  );

  useEffect(() => {
    if (!token) return;

    fetchAlerts({
      fromDate: from,
      toDate: to,
      nextFilters: filters,
    });
  }, [fetchAlerts, filters, from, queryKey, to, token]);

  const selectedAlert = useMemo(
    () => alerts.find((alert) => alert.id === alertId) || null,
    [alertId, alerts]
  );

  const groupedAlerts = useMemo(() => {
    const groups: Record<
      SupportErrorAlertTriageStatus,
      SupportErrorAlertRecord[]
    > = {
      untriaged: [],
      known: [],
      escalated: [],
      resolved: [],
    };

    for (const alert of alerts) {
      const status = alert.triageStatus || "untriaged";
      groups[status].push(alert);
    }

    for (const status of COLUMN_ORDER) {
      groups[status].sort((a, b) =>
        (b.createdAt || "").localeCompare(a.createdAt || "")
      );
    }

    return groups;
  }, [alerts]);

  const updateLocalAlert = useCallback(
    (
      alertId: string,
      updater: (alert: SupportErrorAlertRecord) => SupportErrorAlertRecord
    ) => {
      setAlerts((prev) =>
        prev.map((alert) => (alert.id === alertId ? updater(alert) : alert))
      );
    },
    []
  );

  const applyPreset = useCallback(
    (nextPreset: ErrorAlertsDatePreset) => {
      const range = getErrorAlertsPresetRange(nextPreset);
      navigateUrl({
        preset: nextPreset,
        from: range.from,
        to: range.to,
      });
    },
    [navigateUrl]
  );

  const applyFilters = useCallback(
    (nextFilters: SupportErrorAlertSearchFilter[]) => {
      navigateUrl({ filters: nextFilters });
    },
    [navigateUrl]
  );

  const applyFilterFromDetail = useCallback(
    (field: ErrorAlertSearchFieldId, value: string) => {
      const trimmed = value.trim();
      if (!canApplyDetailFilter(trimmed)) return;

      const nextFilters = [
        ...filters.filter((filter) => filter.field !== field),
        { field, value: trimmed },
      ];
      navigateUrl({ filters: nextFilters });
    },
    [filters, navigateUrl]
  );

  const applyCustomDateRange = useCallback(
    (range: { from: Date; to: Date }) => {
      navigateUrl({
        preset: "custom",
        from: range.from,
        to: range.to,
      });
    },
    [navigateUrl]
  );

  const applyTimelineBucket = useCallback(
    (bucketFrom: Date, bucketTo: Date) => {
      setSelectedBucketStart(bucketFrom.toISOString());
      navigateUrl({
        preset: "custom",
        from: bucketFrom,
        to: bucketTo,
      });
    },
    [navigateUrl]
  );

  const changeTriageStatus = useCallback(
    async (
      alertId: string,
      triageStatus: SupportErrorAlertTriageStatus,
      previousStatus?: SupportErrorAlertTriageStatus
    ) => {
      if (!token) return;

      const rollbackStatus =
        previousStatus ??
        alerts.find((alert) => alert.id === alertId)?.triageStatus;
      if (!rollbackStatus || rollbackStatus === triageStatus) return;

      updateLocalAlert(alertId, (alert) => ({ ...alert, triageStatus }));
      setSavingAlertId(alertId);

      try {
        await supportUpdateErrorAlertTriage(token, alertId, triageStatus);
      } catch (error) {
        updateLocalAlert(alertId, (alert) => ({
          ...alert,
          triageStatus: rollbackStatus,
        }));
        handleError(error, "Failed to update triage status");
      } finally {
        setSavingAlertId(null);
      }
    },
    [alerts, handleError, token, updateLocalAlert]
  );

  const updateVisibility = useCallback(
    async (alert: SupportErrorAlertRecord, isVisibleToUser: boolean) => {
      if (!token) return;

      setSavingAlertId(alert.id);
      try {
        await supportUpdateErrorAlertVisibility(
          token,
          alert.id,
          isVisibleToUser
        );
        updateLocalAlert(alert.id, (current) => ({
          ...current,
          isVisibleToUser,
        }));
      } catch (error) {
        handleError(error, "Failed to update user notification visibility");
      } finally {
        setSavingAlertId(null);
      }
    },
    [handleError, token, updateLocalAlert]
  );

  const copyShareLink = useCallback(async (alert: SupportErrorAlertRecord) => {
    const url = buildErrorAlertShareUrl(alert.id, alert.createdAt);

    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard.");
    } catch {
      toast.error("Failed to copy link to clipboard.");
    }
  }, []);

  const sendToSlack = useCallback(
    async (note: string) => {
      if (!token || !selectedAlert) return;

      try {
        const result = await supportSendErrorAlertToSlack(
          token,
          selectedAlert.id,
          note.trim() || undefined
        );
        updateLocalAlert(selectedAlert.id, (alert) => ({
          ...alert,
          sentToSlack: result.data.sentToSlack,
          triageStatus: result.data.triageStatus,
          userEmail: result.data.userEmail || alert.userEmail,
        }));
        toast.success(
          result.data.warning || "Error alert sent to Slack successfully."
        );
      } catch (error) {
        handleError(error, "Failed to send error alert to Slack");
        throw error;
      }
    },
    [handleError, selectedAlert, token, updateLocalAlert]
  );

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!over) {
        setActiveDragAlertId(null);
        return;
      }

      const nextStatus = over.id as SupportErrorAlertTriageStatus;
      if (!COLUMN_ORDER.includes(nextStatus)) {
        setActiveDragAlertId(null);
        return;
      }

      const currentAlert = alerts.find((alert) => alert.id === active.id);
      if (!currentAlert || currentAlert.triageStatus === nextStatus) {
        setActiveDragAlertId(null);
        return;
      }

      changeTriageStatus(
        currentAlert.id,
        nextStatus,
        currentAlert.triageStatus
      );
      setActiveDragAlertId(null);
    },
    [alerts, changeTriageStatus]
  );

  if (!token) return null;

  const initialLoading = loading && alerts.length === 0;
  const refreshing = loading && alerts.length > 0;
  const detailSaving =
    selectedAlert != null && savingAlertId === selectedAlert.id;

  return (
    <Box className={classes.page}>
      <Paper className={classes.paper}>
        <Typography variant="h6" gutterBottom>
          Error Alerts
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          Review, triage, escalate, and clear user-facing integration alerts.
        </Typography>

        <Box className={classes.filtersRow}>
          <Button
            variant={preset === "day" ? "contained" : "outlined"}
            color="primary"
            disabled={loading}
            onClick={() => applyPreset("day")}
          >
            Current day
          </Button>
          <Button
            variant={preset === "week" ? "contained" : "outlined"}
            color="primary"
            disabled={loading}
            onClick={() => applyPreset("week")}
          >
            Current week
          </Button>
          <Button
            variant={preset === "month" ? "contained" : "outlined"}
            color="primary"
            disabled={loading}
            onClick={() => applyPreset("month")}
          >
            Current month
          </Button>
          <Button
            variant={preset === "custom" ? "contained" : "outlined"}
            color="primary"
            disabled={loading}
            onClick={() => navigateUrl({ preset: "custom", from, to })}
          >
            Custom
          </Button>
        </Box>

        <Box className={classes.filtersRow}>
          <ErrorAlertsSearchFilters
            filters={filters}
            onApply={applyFilters}
            applying={loading}
          />
        </Box>

        {preset === "custom" ? (
          <ErrorAlertsCustomDateRange
            from={from}
            to={to}
            disabled={loading}
            onApply={applyCustomDateRange}
          />
        ) : null}

        {initialLoading ? (
          <Box className={classes.loading}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <ErrorAlertsTimeline
              timeline={timeline}
              loading={loading && !timeline}
              selectedBucketStart={selectedBucketStart}
              onSelectBucket={applyTimelineBucket}
            />

            <Box className={classes.boardToolbar}>
              <Box className={classes.cardLegend}>
                <Box className={classes.legendItem}>
                  <Box
                    className={clsx(
                      classes.legendSwatch,
                      classes.legendSwatchNew
                    )}
                  />
                  <Typography variant="caption" color="textSecondary">
                    New — visible to user
                  </Typography>
                </Box>
                <Box className={classes.legendItem}>
                  <Box
                    className={clsx(
                      classes.legendSwatch,
                      classes.legendSwatchNotNew
                    )}
                  />
                  <Typography variant="caption" color="textSecondary">
                    Not new — hidden from user
                  </Typography>
                </Box>
              </Box>
              <Box className={classes.boardSummary}>
                <Typography variant="body2" color="textSecondary">
                  Showing {alerts.length} alert{alerts.length === 1 ? "" : "s"}
                  {nextCursor ? "+" : ""}
                </Typography>
                {nextCursor ? (
                  <LoadableButton
                    variant="text"
                    color="primary"
                    size="small"
                    loading={loadingMore}
                    onClick={() =>
                      fetchAlerts({
                        fromDate: from,
                        toDate: to,
                        cursor: nextCursor,
                        nextFilters: filters,
                      })
                    }
                  >
                    Load more
                  </LoadableButton>
                ) : null}
              </Box>
            </Box>

            <Box
              className={clsx(
                classes.boardArea,
                refreshing && classes.boardRefreshing
              )}
            >
              {refreshing ? (
                <Box className={classes.boardOverlay}>
                  <CircularProgress size={28} />
                </Box>
              ) : null}

              <DndContext
                sensors={sensors}
                onDragStart={({ active }) =>
                  setActiveDragAlertId(String(active.id))
                }
                onDragEnd={handleDragEnd}
                onDragCancel={() => setActiveDragAlertId(null)}
              >
                <Box className={classes.boardScroll}>
                  {COLUMN_ORDER.map((status) => (
                    <ErrorAlertsColumn
                      key={status}
                      status={status}
                      alerts={groupedAlerts[status]}
                      onSelect={(alert) => navigateUrl({ alertId: alert.id })}
                    />
                  ))}
                </Box>
                <DragOverlay dropAnimation={null}>
                  {activeDragAlertId ? (
                    <Box width={320}>
                      <ErrorAlertCardContent
                        alert={
                          alerts.find(
                            (alert) => alert.id === activeDragAlertId
                          )!
                        }
                        className={classes.cardDragging}
                      />
                    </Box>
                  ) : null}
                </DragOverlay>
              </DndContext>
            </Box>
          </>
        )}
      </Paper>

      <Drawer
        anchor="right"
        open={!!selectedAlert}
        onClose={() => navigateUrl({ alertId: null })}
        classes={{ paper: classes.drawerPaper }}
      >
        {selectedAlert ? (
          <>
            <Box className={classes.detailHeader}>
              <Typography variant="h6">Error alert details</Typography>
              <IconButton
                onClick={() => navigateUrl({ alertId: null })}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Box className={classes.detailBody}>
              <Box className={classes.detailTopControls}>
                <TextField
                  select
                  variant="outlined"
                  size="small"
                  margin="dense"
                  label="Triage status"
                  value={selectedAlert.triageStatus}
                  disabled={detailSaving}
                  className={classes.detailTopField}
                  onChange={(event) => {
                    const nextStatus = event.target
                      .value as SupportErrorAlertTriageStatus;
                    changeTriageStatus(
                      selectedAlert.id,
                      nextStatus,
                      selectedAlert.triageStatus
                    );
                  }}
                  SelectProps={{ native: true }}
                >
                  {COLUMN_ORDER.map((status) => (
                    <option key={status} value={status}>
                      {COLUMN_LABELS[status]}
                    </option>
                  ))}
                </TextField>

                <FormControlLabel
                  label="New"
                  className={classes.detailTopSwitch}
                  control={
                    <Switch
                      color="primary"
                      checked={selectedAlert.isVisibleToUser}
                      disabled={detailSaving}
                      onChange={(_, checked) => {
                        if (checked === selectedAlert.isVisibleToUser) return;
                        setPendingVisibilityChange({
                          alert: selectedAlert,
                          isVisibleToUser: checked,
                        });
                      }}
                    />
                  }
                />
              </Box>

              <Box className={classes.detailSection}>
                <Box className={classes.detailActionRow}>
                  <Button
                    variant="contained"
                    color="primary"
                    className={clsx(
                      classes.detailActionButton,
                      classes.detailPrimaryAction
                    )}
                    disabled={detailSaving || !!selectedAlert.sentToSlack}
                    onClick={() => setSlackDialogOpen(true)}
                  >
                    Send to Slack
                  </Button>
                  <Tooltip title="Copy link to clipboard">
                    <span className={classes.shareButtonWrap}>
                      <Button
                        variant="contained"
                        color="primary"
                        className={clsx(
                          classes.detailActionButton,
                          classes.shareButton
                        )}
                        disabled={detailSaving}
                        onClick={() => copyShareLink(selectedAlert)}
                      >
                        <ShareIcon fontSize="small" />
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
                {selectedAlert.sentToSlack ? (
                  <Typography variant="body2" color="textSecondary">
                    Already sent {formatRelativeTime(selectedAlert.sentToSlack)}
                    .
                  </Typography>
                ) : null}
              </Box>

              <DetailFilterField
                label="User"
                value={selectedAlert.userEmail || selectedAlert.user}
                filterField="user"
                filterValue={selectedAlert.userEmail || selectedAlert.user}
                onApplyFilter={applyFilterFromDetail}
                disabled={loading}
              />
              <TextField
                label="UID"
                fullWidth
                variant="outlined"
                size="small"
                margin="dense"
                value={selectedAlert.user}
                InputProps={{ readOnly: true }}
              />
              <DetailFilterField
                label="Platform"
                value={selectedAlert.platform || "Unknown"}
                filterField="platform"
                filterValue={selectedAlert.platform || ""}
                onApplyFilter={applyFilterFromDetail}
                disabled={loading}
              />
              {selectedAlert.type ? (
                <DetailFilterField
                  label="Alert type"
                  value={getErrorAlertFilterValueLabel(
                    "type",
                    selectedAlert.type
                  )}
                  filterField="type"
                  filterValue={selectedAlert.type}
                  onApplyFilter={applyFilterFromDetail}
                  disabled={loading}
                />
              ) : null}
              {selectedAlert.errorCode ? (
                <DetailFilterField
                  label="Error identifier"
                  value={getErrorAlertFilterValueLabel(
                    "error",
                    selectedAlert.errorCode
                  )}
                  filterField="error"
                  filterValue={selectedAlert.errorCode}
                  onApplyFilter={applyFilterFromDetail}
                  disabled={loading}
                  helperText="Internal slug set by the backend in the error field to categorize a specific failure (not an HTTP status code)."
                />
              ) : null}
              <TextField
                label="Received"
                fullWidth
                variant="outlined"
                size="small"
                margin="dense"
                value={formatDateTime(selectedAlert.createdAt)}
                InputProps={{ readOnly: true }}
              />
              <DetailFilterField
                label="Integration"
                value={selectedAlert.integrationId || "Not provided"}
                filterField="integration"
                filterValue={selectedAlert.integrationId || ""}
                onApplyFilter={applyFilterFromDetail}
                disabled={loading}
              />

              <TextField
                label="What the user saw"
                fullWidth
                variant="outlined"
                size="small"
                margin="dense"
                multiline
                minRows={2}
                value={
                  selectedAlert.message || "No user-facing message captured."
                }
                InputProps={{ readOnly: true }}
              />

              <TextField
                label="Technical error"
                fullWidth
                variant="outlined"
                size="small"
                margin="dense"
                multiline
                minRows={3}
                className={classes.readonlyField}
                value={
                  selectedAlert.errorMessage ||
                  "No technical error message captured."
                }
                InputProps={{ readOnly: true }}
              />

              <TextField
                label="Stack trace"
                fullWidth
                variant="outlined"
                size="small"
                margin="dense"
                multiline
                minRows={4}
                className={classes.readonlyField}
                value={selectedAlert.stack || "No stack trace captured."}
                InputProps={{ readOnly: true }}
              />

              {Object.keys(selectedAlert.extraFields || {}).length > 0 ? (
                <TextField
                  label="Additional fields"
                  fullWidth
                  variant="outlined"
                  size="small"
                  margin="dense"
                  multiline
                  minRows={3}
                  className={classes.readonlyField}
                  value={JSON.stringify(selectedAlert.extraFields, null, 2)}
                  InputProps={{ readOnly: true }}
                />
              ) : null}
            </Box>
          </>
        ) : null}
      </Drawer>

      <ConfirmDialog
        open={!!pendingVisibilityChange}
        title={
          pendingVisibilityChange?.isVisibleToUser
            ? "Mark this alert as new for the user?"
            : "Hide this alert from the user?"
        }
        text={
          pendingVisibilityChange?.isVisibleToUser
            ? "This will show the error notification to the user again in the app. Only do this if they should still be aware of this issue."
            : "This will remove the error from the user's view. Have you confirmed that the problem has been fully resolved for them? If their data is still incomplete, leave the alert up so they still see the warning."
        }
        cancel="Cancel"
        confirm={
          pendingVisibilityChange?.isVisibleToUser
            ? "Yes, show to user"
            : "Yes, hide from user"
        }
        onCancel={() => setPendingVisibilityChange(null)}
        onConfirm={async () => {
          if (!pendingVisibilityChange) return;
          await updateVisibility(
            pendingVisibilityChange.alert,
            pendingVisibilityChange.isVisibleToUser
          );
          setPendingVisibilityChange(null);
        }}
      />

      <SendToSlackDialog
        alert={selectedAlert}
        open={slackDialogOpen}
        onClose={() => setSlackDialogOpen(false)}
        onSent={sendToSlack}
      />
    </Box>
  );
}

export default ErrorAlerts;
